import React from "react";
import type { Box } from "./frame";
import { easeInOutCubic, settle } from "./anim";

/**
 * Anivaze v4 — the WHITEBOARD DOODLE draw-on (the InstaDoodle/Doodly/VideoScribe
 * signature, as pure f(t)): a hand holding a marker sweeps a zigzag over the
 * beat's visual while a clip-path reveals it row by row — the artwork looks
 * hand-sketched onto the board. When the sketch completes, the hand lifts away.
 * Deterministic, $0/frame; applied automatically by beats.tsx for the "doodle"
 * theme, over EVERY visual kind (diagrams, characters, charts, images…).
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

const ROWS = 5;

/** A simple vector hand holding a marker; the marker TIP is at (0,0). */
export const DoodleHand: React.FC<{ scale: number }> = ({ scale: s }) => (
  <g transform={`scale(${s})`}>
    {/* soft shadow under the hand */}
    <ellipse cx={26} cy={30} rx={30} ry={12} fill="#000" opacity={0.08} />
    {/* marker body (angled, tip at origin) */}
    <path d="M 0 0 L 14 18 L 22 12 L 6 -4 Z" fill="#2464c7" />
    <path d="M 0 0 L 5 6 L 9 3 L 3 -3 Z" fill="#1a2330" />
    {/* fist */}
    <path d="M 12 14 Q 8 26 20 34 Q 34 42 48 34 Q 58 28 54 16 Q 50 6 38 6 Q 30 4 24 8 Q 16 8 12 14 Z" fill="#e8b48c" stroke="#1a2330" strokeWidth={2.4} strokeLinejoin="round" />
    {/* thumb over the marker */}
    <path d="M 16 12 Q 22 2 32 6 Q 38 9 34 16 Q 28 20 20 18 Z" fill="#f0c39a" stroke="#1a2330" strokeWidth={2.2} strokeLinejoin="round" />
    {/* finger creases */}
    <path d="M 30 32 Q 36 30 40 26 M 36 34 Q 42 31 46 26" fill="none" stroke="#1a2330" strokeWidth={1.6} strokeLinecap="round" opacity={0.5} />
    {/* cuff */}
    <path d="M 46 34 Q 56 30 58 20 L 66 26 Q 62 40 50 42 Z" fill="#4a72d0" stroke="#1a2330" strokeWidth={2.2} strokeLinejoin="round" />
  </g>
);

/**
 * Wraps a beat visual in the draw-on reveal. The reveal sweeps ROWS horizontal
 * bands in a zigzag (left→right, then right→left…) over `revealDur` seconds,
 * with the hand riding the front; the hand then lifts away with a settle.
 */
export const DrawOnGroup: React.FC<{
  box: Box;
  t: number;
  idSeed: string;
  children: React.ReactNode;
}> = ({ box, t, idSeed, children }) => {
  const revealDur = 1.5;
  const p = easeInOutCubic(clamp01(t / revealDur));
  const clipId = `doodle-${idSeed.replace(/[^a-z0-9_-]/gi, "").slice(0, 24)}`;
  const rowH = box.h / ROWS;
  const full = Math.floor(p * ROWS);
  const q = p * ROWS - full; // progress within the current row
  const rects: React.ReactNode[] = [];
  for (let i = 0; i < Math.min(full, ROWS); i++) {
    rects.push(<rect key={i} x={box.x} y={box.y + i * rowH} width={box.w} height={rowH + 1} />);
  }
  if (full < ROWS) {
    const ltr = full % 2 === 0;
    const w = box.w * q;
    rects.push(
      <rect key="head" x={ltr ? box.x : box.x + box.w - w} y={box.y + full * rowH} width={w} height={rowH + 1} />,
    );
  }
  // Hand at the reveal front (zigzag), with a light scribble jitter; after the
  // sketch completes it lifts toward the bottom-right and fades.
  const done = p >= 1;
  const ltr = full % 2 === 0;
  const frontX = done ? box.x + box.w : ltr ? box.x + box.w * q : box.x + box.w * (1 - q);
  const frontY = done ? box.y + box.h : box.y + Math.min(full, ROWS - 1) * rowH + rowH * 0.55;
  const jitterY = done ? 0 : Math.sin(t * 56) * rowH * 0.06;
  const lift = done ? settle(t - revealDur, 0.5, 0) : 0;
  const handScale = Math.max(1.1, Math.min(box.w, box.h) / 220);
  return (
    <g>
      <defs>
        <clipPath id={clipId}>{rects}</clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{children}</g>
      <g
        opacity={1 - lift}
        transform={`translate(${frontX + lift * box.w * 0.25} ${frontY + jitterY + lift * box.h * 0.3})`}
      >
        <DoodleHand scale={handScale} />
      </g>
    </g>
  );
};
